import { HttpClient } from './httpClient';
import {
  User,
  CreateUserRequest,
  UpdateUserRequest,
  ChangePasswordRequest,
  ResetPasswordRequest,
  ResetPasswordConfirmRequest,
  ApiResponse,
  PaginatedResponse,
  PaginationQuery,
} from '@passport/api-types';

export class UserClient {
  constructor(private http: HttpClient) {}

  // place this in a class level variable and append and avoid repeatation. This can help renaming controller path tomorrow "/users" 

  async getProfile(): Promise<ApiResponse<User>> {
    return this.http.post('/api/v1/iam/self-service/users/me');
  }

  /** Self-service update of the caller's own display name — the only field this endpoint
   *  supports (see the backend's self-service/users/me/update route). Not to be confused
   *  with updateUser(id, ...) below, which is the admin any-user-by-ID endpoint. */
  async updateProfile(userID: number, displayName: string): Promise<ApiResponse<User>> {
    return this.http.post('/api/v1/iam/self-service/users/me/update', { userID, displayName });
  }

  // POST, not GET — BaseUserController's GetAll/GetById actions bind their parameter
  // [FromBody] (both also happen to have an [HttpGet] route registered, but that's a decoy:
  // ASP.NET Core's [ApiController] auto-400s a [FromBody] parameter that arrives with no body,
  // which is exactly what a GET request sends). Same POST-for-list/get convention every other
  // client in this package already uses — see RoleClient.listRoles/getRole.
  async getUser(id: string): Promise<ApiResponse<User>> {
    return this.http.post(`/api/v1/iam/users/${id}`);
  }

  async listUsers(query?: PaginationQuery): Promise<ApiResponse<PaginatedResponse<User>>> {
    return this.http.post('/api/v1/iam/users', query);
  }

  async createUser(request: CreateUserRequest): Promise<ApiResponse<User>> {
    return this.http.post('/api/v1/iam/users/create', request);
  }

  async updateUser(id: string, request: UpdateUserRequest): Promise<ApiResponse<User>> {
    return this.http.put(`/api/v1/iam/users/${id}`, request);
  }

  async deleteUser(id: string): Promise<ApiResponse<void>> {
    return this.http.delete(`/api/v1/iam/users/${id}`);
  }

  async changePassword(request: ChangePasswordRequest): Promise<ApiResponse<void>> {
    return this.http.post('/users/change-password', request);
  }

  async requestPasswordReset(request: ResetPasswordRequest): Promise<ApiResponse<void>> {
    return this.http.post('/api/v1/iam/auth/forgot-password', request);
  }

  async confirmPasswordReset(request: ResetPasswordConfirmRequest): Promise<ApiResponse<void>> {
    return this.http.post('/api/v1/iam/auth/reset-password/complete', request);
  }
}
